import type {CastReceiverContext, PlayerManager} from 'chromecast-caf-receiver/cast.framework';
import type {MediaInformation, QueueItem, Track} from 'chromecast-caf-receiver/cast.framework.messages';

import {HTML5AnalyticsStateMachine} from '../../analyticsStateMachines/HTML5AnalyticsStateMachine';
import {Analytics} from '../../core/Analytics';
import {HeartbeatService} from '../../core/HeartbeatService';
import {Event} from '../../enums/Event';
import {Player} from '../../enums/Player';
import {ErrorDetailBackend} from '../../features/errordetails/ErrorDetailBackend';
import {ErrorDetailTracking} from '../../features/errordetails/ErrorDetailTracking';
import {Feature} from '../../features/Feature';
import {FeatureConfig} from '../../features/FeatureConfig';
import {AnalyticsConfig} from '../../types/AnalyticsConfig';
import {AnalyticsStateMachineOptions} from '../../types/AnalyticsStateMachineOptions';
import {DrmPerformanceInfo} from '../../types/DrmPerformanceInfo';
import {FeatureConfigContainer} from '../../types/FeatureConfigContainer';
import {normalizeVideoDuration, PlaybackInfo} from '../../types/PlaybackInfo';
import {isNotNullish} from '../../utils/Utils';
import {InternalAdapter} from '../internal/InternalAdapter';
import {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

import {CastFrameworkEvent} from './castFrameworkTypes';

export class CAFv3InternalAdapter extends InternalAdapter implements InternalAdapterAPI {
  private readonly playerManager: PlayerManager;

  /** Boolean flag to prevent multiple calls of the {@link Event.UNLOAD} event */
  private onBeforeUnLoadEvent = false;

  /** If defined, it is also used as a flag to determine that chromecast was already playing some content */
  private currentQueueItem: QueueItem | null | undefined;
  private mediaInformation: MediaInformation | undefined;
  private activeAudioTrack: Track | undefined;
  private activeTextTracks: Track[] = [];

  private isSeeking: boolean;

  constructor(castReceiverContext: CastReceiverContext, opts?: AnalyticsStateMachineOptions) {
    super(opts);
    const playingHeartbeatService = new HeartbeatService(() =>
      castReceiverContext.getPlayerManager().getCurrentTimeSec(),
    );
    const stateMachine = new HTML5AnalyticsStateMachine(this.stateMachineCallbacks, playingHeartbeatService, this.opts);
    playingHeartbeatService.setListener(stateMachine);
    this.playerManager = castReceiverContext.getPlayerManager();
    this.stateMachine = stateMachine;
    this.isSeeking = false;
  }

  initialize(analytics: Analytics): Array<Feature<FeatureConfigContainer, FeatureConfig>> {
    this.registerCastFrameworkEventListeners();
    this.registerWindowUnloadEventListeners();

    const errorDetailTracking = new ErrorDetailTracking(
      analytics.errorDetailTrackingSettingsProvider,
      new ErrorDetailBackend(analytics.errorDetailTrackingSettingsProvider.collectorConfig),
      [analytics.errorDetailSubscribable],
      undefined,
    );
    return [errorDetailTracking];
  }

  getPlayerVersion = () => cast?.framework?.VERSION ?? 'unknown';
  getPlayerName = () => Player.CHROMECAST_SHAKA;
  getPlayerTech = () => 'html5';
  getAutoPlay = () => this.currentQueueItem?.autoplay ?? false;
  getDrmPerformanceInfo = (): DrmPerformanceInfo | undefined => this.drmPerformanceInfo;

  sourceChange(config: AnalyticsConfig) {
    this.stateMachine.callManualSourceChangeEvent(config, this.playerManager.getCurrentTimeSec());
  }

  getCurrentPlaybackInfo(): PlaybackInfo {
    const stats = this.playerManager.getStats();

    // filter out undefined and null values
    const subtitleLanguages = this.activeTextTracks?.map((t) => t.language).filter(isNotNullish);

    return {
      isLive: this.getIsLive(),
      playerTech: this.getPlayerTech(),
      videoDuration: normalizeVideoDuration(this.mediaInformation?.duration ?? 0),
      videoPlaybackHeight: stats.height,
      videoPlaybackWidth: stats.width,
      audioCodec: this.activeAudioTrack?.trackContentType,
      audioLanguage: this.activeAudioTrack?.language,
      subtitleLanguage: subtitleLanguages ? subtitleLanguages.join(',') : undefined,
      // TODO: Implement `droppedFrames` tracking in CAFv3
      //  - https://developers.google.com/cast/docs/reference/web_receiver/cast.framework.stats.Stats#droppedFrames
      droppedFrames: 0,
    };
  }

  private registerCastFrameworkEventListeners() {
    // This is a special identifier, which can be used to listen for all events (mostly used for debugging purposes)
    // see: https://developers.google.com/cast/docs/reference/web_receiver/cast.framework.events#.EventType
    // this.playerManager.addEventListener(CastFrameworkEvent.ALL, (event) => {
    //   console.log(`CastFrameworkEvent.${event.type}`, event);
    // });

    // Fired when the browser has finished loading the metadata for a clip
    this.playerManager.addEventListener(CastFrameworkEvent.LOADED_METADATA, (_event) => {
      this.eventCallback(Event.SOURCE_LOADED, {});
    });

    this.playerManager.addEventListener(CastFrameworkEvent.PLAYER_LOAD_COMPLETE, (event) => {
      const audioTracksManager = this.playerManager.getAudioTracksManager();
      this.activeAudioTrack = audioTracksManager.getActiveTrack();

      // captions
      const textTracksManager = this.playerManager.getTextTracksManager();
      this.activeTextTracks = textTracksManager.getActiveTracks();

      const queueManager = this.playerManager.getQueueManager();
      this.currentQueueItem = queueManager?.getCurrentItem();

      this.mediaInformation = event.media;

      this.eventCallback(Event.READY, {});
    });

    // Fired when playback is ready to start (ie: after being paused)
    this.playerManager.addEventListener(CastFrameworkEvent.PLAY, (event) => {
      this.eventCallback(Event.PLAY, {currentTime: event.currentMediaTime});
    });

    // Fired when playback is paused
    this.playerManager.addEventListener(CastFrameworkEvent.PAUSE, (_event) => {
      this.eventCallback(Event.PAUSE, {currentTime: this.currentTime});
    });

    // Fired when an error occurs
    this.playerManager.addEventListener(CastFrameworkEvent.ERROR, (event) => {
      const errorData = isNotNullish(event.error) ? event.error : undefined;
      this.eventCallback(Event.ERROR, {
        currentTime: this.playerManager.getCurrentTimeSec(),
        code: event.detailedErrorCode,
        message: event.reason,
        legacyData: errorData,
        data: {additionalData: JSON.stringify(errorData)},
      });
    });

    // Fired when the media is being seeked
    this.playerManager.addEventListener(CastFrameworkEvent.SEEKING, (event) => {
      this.isSeeking = true;
      this.eventCallback(Event.PAUSE, {currentTime: event.currentMediaTime});
      this.eventCallback(Event.SEEK, {
        currentTime: event.currentMediaTime,
      });
    });

    // Fired when a seek has finished. This will not be fired in case seeking finishes when the media is paused
    this.playerManager.addEventListener(CastFrameworkEvent.SEEKED, (event) => {
      this.isSeeking = false;
      this.eventCallback(Event.SEEKED, {
        currentTime: event.currentMediaTime,
      });
    });

    // Fired when the browser is trying to fetch media data,
    // but did not receive a response. The CastFrameworkEvent.BUFFERING
    // event is implemented consistently across stream types, and should be used instead
    // of 'stalled' when trying to check if the player is buffering.
    this.playerManager.addEventListener(CastFrameworkEvent.STALLED, (_event) => undefined);

    this.playerManager.addEventListener(CastFrameworkEvent.BUFFERING, (event) => {
      if (!this.isSeeking && event.isBuffering) {
        this.eventCallback(Event.START_BUFFERING, {
          currentTime: this.currentTime,
        });
      }
    });

    // Fired periodically while the media is playing. This will fire whenever the currentTime attribute changes
    this.playerManager.addEventListener(CastFrameworkEvent.TIME_UPDATE, (event) => {
      if (!this.isSeeking) {
        this.eventCallback(Event.TIMECHANGED, {
          currentTime: event.currentMediaTime,
        });
      }
    });

    // Fired when a LOAD message is received
    this.playerManager.addEventListener(CastFrameworkEvent.REQUEST_LOAD, (_event) => {
      // this event is triggered whenever new content is to be played
      // it's not necessary to do license call and reset all data if this is the first item being played
      if (this.currentQueueItem) {
        this.eventCallback(Event.PLAYLIST_TRANSITION, {
          currentTime: this.playerManager.getCurrentTimeSec(),
        });
      }
    });
  }

  private registerWindowUnloadEventListeners() {
    const handleWindowUnloadEvent = () => {
      if (!this.onBeforeUnLoadEvent) {
        this.onBeforeUnLoadEvent = true;
        this.eventCallback(Event.UNLOAD, {currentTime: this.currentTime});
      }
      this.release();
    };

    this.windowEventTracker.addEventListener('beforeunload', handleWindowUnloadEvent);
    this.windowEventTracker.addEventListener('unload', handleWindowUnloadEvent);
  }

  protected get currentTime(): number {
    return this.playerManager.getCurrentTimeSec();
  }

  private getIsLive(): boolean | undefined {
    // needs to be explicitly set to "LIVE" by customers in their receiver implementation
    if (this.mediaInformation && this.mediaInformation.streamType === 'LIVE') {
      return true;
    }

    // hack because object exists only for live streams
    const liveSeekableRange = this.playerManager.getLiveSeekableRange();

    if (liveSeekableRange) {
      return true;
    }

    return undefined;
  }
}
